import * as reduxApi from 'src/utils/redux-api-v2';
import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  AllApplicantsFilters,
  SavedDashboardList,
  SelectedAllApplicantsTableFilterColumnsData,
} from 'src/types/analytics-genesis';
import type {DateRangeWithTimezone} from '@spaced-out/ui-design-system/lib/types';
import {thunkify as flow} from 'src/utils/thunks';

import {fetching, key} from 'src/utils/redux';
import {allApplicantsTableColumnsQuery} from 'src/utils/analytics-genesis';


export const SET_CURRENT_DASHBOARD_ID = 'all-applicants/set-current-dashboard';
export const SET_SELECTED_FILTERS = 'all-applicants/set-selected-filters';
export const SET_SELECTED_DATE_RANGE = 'all-applicants/set-selected-date-range';
export const CLEAR_SELECTED_FILTERS = 'all-applicants/clear-selected-filters';
export const RECEIVE_SAVED_DASHBOARDS =
  'all-applicants/receive-saved-dashboards';
export const RECEIVE_ALL_APPLICANTS_TABLE_COLUMNS =
  'all-applicants/receive-all-applicants-table-columns';
export const RECEIVE_TABLE_FILTER_COLUMNS =
  'all-applicants/receive-table-filter-columns';

type SetCurrentDashboardIdAction = {
  type: 'all-applicants/set-current-dashboard';
  payload: string | null | undefined;
};

type SetSelectedFiltersAction = {
  type: 'all-applicants/set-selected-filters';
  payload: AllApplicantsFilters;
};

type SetDateRangeAction = {
  type: 'all-applicants/set-selected-date-range';
  payload: DateRangeWithTimezone;
};

type ReceiveSavedDashboardsAction = {
  type: 'all-applicants/receive-saved-dashboards';
  payload: SavedDashboardList;
};

type ClearSelectedFiltersAction = {
  type: 'all-applicants/clear-selected-filters';
};

type ReceiveAllApplicantsTableColumnAction = {
  type: 'all-applicants/receive-all-applicants-table-columns';
  payload: SelectedAllApplicantsTableFilterColumnsData;
};

type ReceiveTableFilterColumnsAction = {
  type: 'all-applicants/receive-table-filter-columns';
  payload: SelectedAllApplicantsTableFilterColumnsData;
};

export type AllApplicantsActions =
  | SetSelectedFiltersAction
  | ReceiveSavedDashboardsAction
  | SetCurrentDashboardIdAction
  | ClearSelectedFiltersAction
  | SetDateRangeAction
  | ReceiveAllApplicantsTableColumnAction
  | ReceiveTableFilterColumnsAction;

export const setCurrentDashboardId = (
  payload: string,
): SetCurrentDashboardIdAction => ({
  type: SET_CURRENT_DASHBOARD_ID,
  payload,
});

export const setSelectedFilters = (
  payload: AllApplicantsFilters,
): SetSelectedFiltersAction => ({
  type: SET_SELECTED_FILTERS,
  payload,
});

export const setDateRange = (
  payload: DateRangeWithTimezone,
): SetDateRangeAction => ({
  type: SET_SELECTED_DATE_RANGE,
  payload,
});

export const clearSelectedFilters = (): ClearSelectedFiltersAction => ({
  type: CLEAR_SELECTED_FILTERS,
});

export const receiveSavedDashboards = (
  payload: SavedDashboardList,
): ReceiveSavedDashboardsAction => ({
  type: RECEIVE_SAVED_DASHBOARDS,
  payload,
});

export const receiveAllApplicantsTableColumnSelection = (
  payload: SelectedAllApplicantsTableFilterColumnsData,
): ReceiveAllApplicantsTableColumnAction => ({
  type: RECEIVE_ALL_APPLICANTS_TABLE_COLUMNS,
  payload,
});

export const getSavedDashboards: () => ThunkAction<unknown> =
  () => async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.get(`analytics/crm/save-filter`, {
          product: 'crm',
          dashboard: 'all-applicants',
        }),
      );

      const {
        filters,
      }: {
        filters: SavedDashboardList;
      } = response;
      await dispatch(receiveSavedDashboards(filters));

      const defaultDashboard = filters.find(({is_default}) => is_default);
      if (defaultDashboard) {
        const {id = '', filters = {}} = defaultDashboard;
        const {
          // @ts-expect-error - TS2339 - Property 'job_id' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_id = [],
          // @ts-expect-error - TS2339 - Property 'job_source' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_source = [],
          // @ts-expect-error - TS2339 - Property 'job_location' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_location = [],
          // @ts-expect-error - TS2339 - Property 'job_department' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_department = [],
          // @ts-expect-error - TS2339 - Property 'job_recruiter' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          job_recruiter = [],
          // @ts-expect-error - TS2339 - Property 'milestone' does not exist on type 'RecruitmentFilters | CandidateSourcePerformanceFilters | HiringPipelineFilters | NPSFilters | ReferralsFilters'.
          milestone = [],
        } = filters;
        await dispatch(setCurrentDashboardId(id));
        await dispatch(
          setSelectedFilters({
            job_id,
            job_source,
            job_location,
            job_department,
            job_recruiter,
            milestone,
          }),
        );
      }
    } catch (error: any) {
      console.error(
        'Error fetching saved dashboard filter for recruiters:',
        error,
      );
    }
  };

export const getAllApplicantsTableFilterColumn: () => ThunkAction<unknown> =
  flow(
    key(() => 'getAllApplicantsTableFilterColumn'),
    fetching(),
  )(() => async (dispatch: Dispatch) => {
    try {
      const payload = await dispatch(
        reduxApi.get(
          'analytics/crm/column-selection',
          allApplicantsTableColumnsQuery,
        ),
      );
      dispatch(receiveAllApplicantsTableColumnSelection(payload));
    } catch (error: any) {
      console.error('Error fetching all applicants table columns:', error);
    }
  });

export const saveAllApplicantsFilterColumns: (
  columnData: SelectedAllApplicantsTableFilterColumnsData,
) => ThunkAction<unknown> = flow(
  key(
    (columnData) =>
      `saveAllApplicantsFilterColumns-${JSON.stringify(columnData)}`,
  ),
  fetching(),
)(
  (columnData) => async (dispatch: Dispatch) =>
    dispatch(reduxApi.post('analytics/crm/column-selection', columnData, {})),
);
